import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HStack, Text } from '@chakra-ui/react';
import { RiGlobalLine } from 'react-icons/ri';
import { Button } from '@/components/ui/button';
import { MenuContent, MenuRadioItem, MenuRadioItemGroup, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { useI18nEnabled } from '@/contexts/FeatureFlagsContext';
import { languageFlags, languageNames, type SupportedLanguage, supportedLanguages } from '@/libs/i18n';

export const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const isI18nEnabled = useI18nEnabled();

  useEffect(() => {
    if (!isI18nEnabled && i18n.language?.substring(0, 2) !== 'en') {
      i18n.changeLanguage('en');
    }
  }, [isI18nEnabled, i18n]);

  if (!isI18nEnabled) return null;

  const currentLang = (i18n.language?.substring(0, 2) || 'en') as SupportedLanguage;

  const handleLanguageChange = (details: { value: string }) => {
    i18n.changeLanguage(details.value);
    document.documentElement.lang = details.value;
  };

  return (
    <MenuRoot positioning={{ placement: 'bottom-end' }}>
      <MenuTrigger asChild>
        <Button variant='ghost' size='md' aria-label='Select language'>
          <HStack gap={1}>
            <RiGlobalLine fontSize='1.15rem' />
            <Text fontSize='sm' fontWeight='medium'>
              {currentLang.toUpperCase()}
            </Text>
          </HStack>
        </Button>
      </MenuTrigger>
      <MenuContent minW='160px'>
        <MenuRadioItemGroup value={currentLang} onValueChange={handleLanguageChange}>
          {supportedLanguages.map((lang) => (
            <MenuRadioItem key={lang} value={lang} cursor='pointer'>
              <HStack gap={2}>
                <Text fontSize='md'>{languageFlags[lang]}</Text>
                <Text>{languageNames[lang]}</Text>
              </HStack>
            </MenuRadioItem>
          ))}
        </MenuRadioItemGroup>
      </MenuContent>
    </MenuRoot>
  );
};
